import { AbstractControl } from '@angular/forms';
import { EMPTY, merge, Observable, tap } from 'rxjs';
import { FieldKey, RoundFn } from '@marketiq/calc-core';

/** Output writes are silent so a result never re-triggers another listener (loop guard). */
const SILENT = { emitEvent: false } as const;

/** Pure two-input aggregate kernel (e.g. calc-core `potentialGrossIncome` / `grossInvestmentAmount`). */
export type AggregateKernel = (
  a: number | null | undefined,
  b: number | null | undefined
) => number | null;

/** Mirror of the host `ctrlChanged`: a calc write marks the result dirty + touched. */
function markChanged(control: AbstractControl | null | undefined): void {
  control?.markAsDirty();
  control?.markAsTouched();
}

/**
 * Binds a two-input "aggregate" sale calculation:
 *   - PGI = Passing Rent (yearly) + Vacant Estimated Rental Amount
 *   - GIA = Actual Sale Price + Buyer Cost
 *
 * Recomputes whenever either input value OR either source UOM changes, then writes (silently):
 *   - the rounded result value, or `null` when the kernel returns null (both inputs null/0), and
 *   - the result UOM: first non-empty source UOM, else the existing result UOM (null on clear),
 * finally marking the result value + UOM dirty/touched.
 *
 * Byte-equivalent to the host `calculatePotentialGrossIncome` / `calculateGrossInvestmentAmount`
 * + `ctrlChanged`. The result format is the host's `'0.2-2'` fallback (these columns carry no
 * `attribute_precision`), supplied here via {@link RoundFn}; Poland uses the corrected rounding.
 *
 * Returns `EMPTY` (a no-op stream) when either input value or the result control is absent —
 * matching the host's `isEveryControlNotNull([valueA, valueB, resultValue])` guard.
 */
export function bindSaleAggregate(
  valueA: AbstractControl | null,
  valueB: AbstractControl | null,
  resultValue: AbstractControl | null,
  uomA: AbstractControl | null,
  uomB: AbstractControl | null,
  resultUom: AbstractControl | null,
  kernel: AggregateKernel,
  round: RoundFn,
  resultField?: FieldKey
): Observable<unknown> {
  if (!valueA || !valueB || !resultValue) {
    return EMPTY;
  }

  const triggers: Observable<unknown>[] = [valueA.valueChanges, valueB.valueChanges];
  if (uomA) {
    triggers.push(uomA.valueChanges);
  }
  if (uomB) {
    triggers.push(uomB.valueChanges);
  }

  return merge(...triggers).pipe(
    tap(() => {
      const value = kernel(valueA.value, valueB.value);
      if (value === null) {
        resultValue.setValue(null, SILENT);
        resultUom?.setValue(null, SILENT);
      } else {
        resultValue.setValue(round(value, resultField), SILENT);
        if (resultUom) {
          resultUom.setValue(uomA?.value || uomB?.value || resultUom.value, SILENT);
        }
      }
      markChanged(resultValue);
      markChanged(resultUom);
    })
  );
}
